import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {CircleMarker, FeatureGroup, MapContainer, Marker, Polyline, Tooltip, useMap} from 'react-leaflet';
import L, {divIcon} from 'leaflet';
import '../../../../node_modules/leaflet/dist/leaflet.css';
import CustomTileLayer from '../layout/map/CustomTileLayer.js';
import {useGetTraceJourneeQuery} from '../../../modules/simulations/simulations.api';
import {COULEUR_NON_AFFECTE, COULEUR_PATRIMOINE, cleCalque, traceDeLEquipe} from './atelierRules';
import TraceJournee from './TraceJournee.js';
import {morceauxTrace, opaciteCalque} from './traceRules.js';
import './carte.css';

/**
 * Carte de l'atelier (Leaflet) : calques patrimoine (gris), collecte (couleur par jour, parité par le style du point) et
 * tournées (points numérotés de la couleur de la journée, tracé facultatif, trajet base en pointillé), visites non
 * affectées en rouge. Aucune règle ici : les calques arrivent calculés par `atelierRules`.
 * AJOUT À VALIDER (GUIDE_UI) : carte plein volet avec calques superposables.
 */

const iconsNumero = new Map();
function iconNumero(numero, couleur, fige, premierDernier = false) {
    const cle = `${numero}|${couleur}|${fige}|${premierDernier}`;
    if (!iconsNumero.has(cle)) {
        iconsNumero.set(cle, divIcon({
            className: 'simulation-marker',
            html: `<span style="display:flex;align-items:center;justify-content:center;min-width:24px;height:24px;padding:0 3px;box-sizing:border-box;border-radius:12px;background:${couleur};color:#fff;font-weight:700;font-size:11px;border:2px solid ${fige ? '#E0A100' : '#fff'}${premierDernier ? `;box-shadow:0 0 0 3px ${couleur}` : ''}">${numero}</span>`,
            iconSize: [24, 24],
            iconAnchor: [12, 12],
        }));
    }
    return iconsNumero.get(cle);
}

const iconBase = divIcon({
    className: 'simulation-marker',
    html: '<span style="display:flex;align-items:center;justify-content:center;width:26px;height:26px;border-radius:6px;background:#fff;color:#213067;font-weight:800;font-size:12px;border:2px solid #213067">B</span>',
    iconSize: [26, 26],
    iconAnchor: [13, 13],
});

/** Style d'un point de collecte : plein = hebdo, anneau = paire, mi-teinte pointillée = impaire. */
export function styleCollecte(couleur, frequence) {
    if (frequence === 'paire') return {color: couleur, fillColor: '#fff', fillOpacity: 1, weight: 3};
    if (frequence === 'impaire') return {color: couleur, fillColor: couleur, fillOpacity: 0.35, weight: 2, dashArray: '2 3'};
    return {color: '#fff', fillColor: couleur, fillOpacity: 0.9, weight: 1};
}

/** Une carte masquée (`display: none`) mesure 0 × 0 : jamais d'`invalidateSize` ni de cadrage dans cet état (Leaflet saute au zoom max). */
export const carteVisible = (conteneur) => Boolean(conteneur) && conteneur.clientWidth > 0 && conteneur.clientHeight > 0;

export const DELAI_STABILISATION_MS = 200;

const cadrer = (map, positions, zoomSeul) => {
    if (positions.length === 1) map.setView(positions[0], zoomSeul, {animate: false});
    else if (positions.length > 1) map.fitBounds(L.latLngBounds(positions), {padding: [40, 40], animate: false});
};

/**
 * Cadrage de la carte, qui reste montée sous les onglets et dont le conteneur change de taille (volet, onglet, bloc journée
 * qui se déplie). Un ResizeObserver recalcule la taille de Leaflet dès que le conteneur est visible. Un cadrage demandé carte
 * masquée est gardé et rejoué quand elle redevient visible ; il est rejoué à chaque redimensionnement tant que la taille n'est
 * pas stabilisée (DELAI_STABILISATION_MS sans changement), pour ne pas se caler sur une hauteur périmée.
 * Deux demandes : le patrimoine / scénario affiché (`cadrageCle`) et une journée (`focus` = {positions, nonce}).
 */
function Cadrage({positions, cadrageCle, focus}) {
    const map = useMap();
    const attente = useRef(null);
    const minuteur = useRef(null);

    const stabiliser = useCallback(() => {
        clearTimeout(minuteur.current);
        minuteur.current = setTimeout(() => { attente.current = null; }, DELAI_STABILISATION_MS);
    }, []);

    // Demande de cadrage : tout de suite si la carte est visible (taille à jour d'abord), sinon gardée pour son retour.
    const demander = useCallback((fn) => {
        clearTimeout(minuteur.current);
        attente.current = fn;
        if (carteVisible(map.getContainer())) {
            map.invalidateSize({animate: false});
            fn();
            stabiliser();
        }
    }, [map, stabiliser]);

    useEffect(() => {
        const conteneur = map.getContainer();
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observeur = new ResizeObserver(() => {
            if (!carteVisible(conteneur)) return;
            map.invalidateSize({animate: false});
            if (attente.current) {
                attente.current();
                stabiliser();
            }
        });
        observeur.observe(conteneur);
        return () => {
            observeur.disconnect();
            clearTimeout(minuteur.current);
        };
    }, [map, stabiliser]);

    // Le cadrage ne suit pas les bascules de calques : seulement le patrimoine / scénario affiché, et l'arrivée tardive des PAV
    // (de 0 à N positions). Une fois fait pour une clé, un zoom manuel n'est plus écrasé.
    const cleDePositions = positions.length === 0 ? null : `${cadrageCle}|${positions.length}|${positions[0]}`;
    const dejaCadre = useRef(null);
    useEffect(() => {
        if (!cleDePositions || dejaCadre.current === cleDePositions) return;
        dejaCadre.current = cleDePositions;
        demander(() => cadrer(map, positions, 14));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, cleDePositions]);

    useEffect(() => {
        if (!focus || focus.positions.length === 0) return;
        demander(() => cadrer(map, focus.positions, 14));
    }, [map, focus, demander]);
    return null;
}

/**
 * Un seul gestionnaire de clic par calque : le FeatureGroup propage les clics de ses marqueurs (`propagatedFrom`), qui portent
 * leur `nomenclature` (et la journée pour les tournées) dans leurs options. Chaque calque est mémoïsé : une bascule ou
 * une frappe dans le volet ne redessine pas les autres.
 */
const groupeClic = (onSelectPav) => ({
    click: (e) => {
        const o = e.propagatedFrom?.options;
        if (o?.nomenclature) onSelectPav(o.nomenclature, o.journee ?? null);
    },
});

const CalquePatrimoine = React.memo(function CalquePatrimoine({pavs, onSelectPav}) {
    const handlers = useMemo(() => groupeClic(onSelectPav), [onSelectPav]);
    return (
        <FeatureGroup eventHandlers={handlers}>
            {pavs.map((p) => (
                <CircleMarker key={p.nomenclature} nomenclature={p.nomenclature} center={[p.lat, p.lon]} radius={4}
                              pathOptions={{color: '#fff', weight: 1, fillColor: COULEUR_PATRIMOINE, fillOpacity: 0.85}}>
                    <Tooltip>{p.nomenclature}</Tooltip>
                </CircleMarker>
            ))}
        </FeatureGroup>
    );
});

const CalqueCollecte = React.memo(function CalqueCollecte({calque, parNomenclature, onSelectPav}) {
    const handlers = useMemo(() => groupeClic(onSelectPav), [onSelectPav]);
    const style = useMemo(() => styleCollecte(calque.couleur, calque.frequence), [calque]);
    return (
        <FeatureGroup eventHandlers={handlers}>
            {calque.nomenclatures.map((n) => {
                const p = parNomenclature.get(n);
                return p && (
                    <CircleMarker key={n} nomenclature={n} center={[p.lat, p.lon]} radius={6} pathOptions={style}>
                        <Tooltip>{n} · {calque.libelle.split(' · ').slice(0, 2).join(' · ')}</Tooltip>
                    </CircleMarker>
                );
            })}
        </FeatureGroup>
    );
});

const CalqueTournee = React.memo(function CalqueTournee({calque, simulationId, avecTraces, active, facteur, onSurvol, onSelectPav}) {
    const handlers = useMemo(() => ({
        ...groupeClic(onSelectPav),
        mouseover: () => onSurvol(calque.cle),
        mouseout: () => onSurvol(null),
    }), [onSelectPav, onSurvol, calque.cle]);
    const handlersTrace = useMemo(() => ({mouseover: () => onSurvol(calque.cle), mouseout: () => onSurvol(null)}), [onSurvol, calque.cle]);
    // Lot 8 : la géométrie routière (base → visites → base) remplace le tracé droit. Sinon, lignes droites.
    // currentData : jamais le tracé d'un autre scénario pendant le chargement (la clé du calque, date|équipe, peut être la même).
    const {currentData} = useGetTraceJourneeQuery({id: simulationId, date: calque.date}, {skip: !avecTraces || !simulationId});
    const route = useMemo(() => traceDeLEquipe(currentData, calque.equipe), [currentData, calque.equipe]);
    const routier = avecTraces && route?.routier && route.positions.length > 1;
    // Lot 10 : aller / milieu / retour (casing, décalage, flèches) ; sans « tracés », seuls l'aller et le retour.
    const morceaux = useMemo(() => morceauxTrace({route: routier ? route.positions : null, calque}), [routier, route, calque]);
    const dernier = calque.points.length - 1;
    return (
        <>
            <TraceJournee morceaux={morceaux} couleur={calque.couleur} actif={active} facteur={facteur} avecMilieu={avecTraces} eventHandlers={handlersTrace}/>
            <FeatureGroup eventHandlers={handlers}>
                {calque.points.map((p, i) => (
                    <Marker key={p.numero} nomenclature={p.nomenclature} journee={calque.cle} position={p.position} opacity={facteur}
                            icon={iconNumero(p.numero, calque.couleur, p.fige, i === 0 || i === dernier)}>
                        <Tooltip>{p.numero}. {p.nomenclature}{p.arrivee ? ` · ${p.arrivee}` : ''}</Tooltip>
                    </Marker>
                ))}
            </FeatureGroup>
        </>
    );
});

const CalqueNonAffectes = React.memo(function CalqueNonAffectes({points, onSelectPav}) {
    const handlers = useMemo(() => groupeClic(onSelectPav), [onSelectPav]);
    return (
        <FeatureGroup eventHandlers={handlers}>
            {points.map((n) => (
                <CircleMarker key={n.nomenclature} nomenclature={n.nomenclature} center={n.position} radius={6}
                              pathOptions={{color: '#fff', weight: 1.5, fillColor: COULEUR_NON_AFFECTE, fillOpacity: 0.95}}>
                    <Tooltip>{n.nomenclature} · non affecté</Tooltip>
                </CircleMarker>
            ))}
        </FeatureGroup>
    );
});

function AtelierCarte({
    pavs, calquesCollecte, calquesTournees, nonAffectes, visible, avecTraces, selection, onSelectPav,
    journeeActive, cadrageCle, simulationId, focus,
}) {
    const [survol, setSurvol] = useState(null);
    const tourneesVisibles = (calquesTournees || []).filter((c) => visible(cleCalque.tournee(c.cle)));
    // Un calque démonté sous le curseur n'émet pas de mouseout : le survol ne compte que s'il porte sur une journée encore affichée.
    const survolEffectif = tourneesVisibles.some((c) => c.cle === survol) ? survol : null;
    const parNomenclature = useMemo(() => new Map((pavs || []).map((p) => [p.nomenclature, p])), [pavs]);
    const positions = useMemo(() => (pavs || []).map((p) => [p.lat, p.lon]), [pavs]);
    const basePos = useMemo(() => (calquesTournees || []).map((c) => c.depart).find(Boolean) ?? null, [calquesTournees]);
    const positionChoisie = useMemo(() => {
        if (!selection) return null;
        const p = parNomenclature.get(selection);
        if (p) return [p.lat, p.lon];
        return (calquesTournees || []).flatMap((c) => c.points).find((x) => x.nomenclature === selection)?.position
            ?? (nonAffectes || []).find((n) => n.nomenclature === selection)?.position ?? null;
    }, [selection, parNomenclature, calquesTournees, nonAffectes]);

    return (
        <MapContainer center={[46.6, 2.4]} zoom={6} preferCanvas zoomAnimation={false} fadeAnimation={false} markerZoomAnimation={false} style={{height: '100%', width: '100%', minHeight: 320}}>
            <CustomTileLayer maxZoom={19} className="tuiles-estompees"/>
            <Cadrage positions={positions} cadrageCle={cadrageCle} focus={focus}/>

            {visible(cleCalque.patrimoine) && <CalquePatrimoine pavs={pavs || []} onSelectPav={onSelectPav}/>}

            {(calquesCollecte || []).filter((c) => visible(cleCalque.collecte(c.cle))).map((c) => (
                <CalqueCollecte key={`c-${c.cle}`} calque={c} parNomenclature={parNomenclature} onSelectPav={onSelectPav}/>
            ))}

            {tourneesVisibles.map((c) => (
                <CalqueTournee key={`t-${c.cle}`} calque={c} simulationId={simulationId} avecTraces={avecTraces} active={journeeActive === c.cle} facteur={opaciteCalque({cle: c.cle, survol: survolEffectif, active: journeeActive === c.cle})} onSurvol={setSurvol} onSelectPav={onSelectPav}/>
            ))}

            {(nonAffectes || []).length > 0 && <CalqueNonAffectes points={nonAffectes} onSelectPav={onSelectPav}/>}

            {basePos && <Marker position={basePos} icon={iconBase}><Tooltip>Base de départ</Tooltip></Marker>}

            {positionChoisie &&
                <CircleMarker center={positionChoisie} radius={13} interactive={false}
                              pathOptions={{color: '#0F2D69', weight: 3, fill: false}}/>}
        </MapContainer>
    );
}

export default React.memo(AtelierCarte);
